// Cookie consent + Google Consent Mode v2 helpers.
// Geo-aware defaults: opt-in (granted only after explicit consent) for users
// in EU/UK/IN/CA/Brazil/Switzerland; opt-out (granted by default until the
// user opts out) for the rest of the world. Consent state is persisted to
// localStorage and dispatched via a CustomEvent so analytics + sentry can
// initialize lazily once the user has agreed.

export const CONSENT_STORAGE_KEY = "rb_consent_v1";
export const CONSENT_POLICY_VERSION = "2026-05-03";
export const CONSENT_EVENT = "rb:consent-changed";

export type ConsentCategory = "necessary" | "analytics" | "marketing" | "preferences";

export interface ConsentState {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
  policyVersion: string;
  decidedAt: string | null;
  region: string;
  source: "explicit" | "implicit-default" | "withdrawn";
}

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const OPT_IN_REGIONS = new Set([
  "EU", "UK", "GB", "IN", "BR", "CH",
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR",
  "DE", "GR", "HU", "IE", "IT", "LV", "LT", "LU", "MT", "NL",
  "PL", "PT", "RO", "SK", "SI", "ES", "SE", "IS", "LI", "NO",
]);

function detectRegion(): string {
  if (typeof navigator === "undefined") return "OTHER";
  const lang = (navigator.language || "").toUpperCase();
  const m = lang.match(/-([A-Z]{2})$/);
  if (m) return m[1];
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  if (tz.startsWith("Asia/Kolkata") || tz.startsWith("Asia/Calcutta")) return "IN";
  if (tz.startsWith("Europe/")) return "EU";
  if (tz.startsWith("America/Los_Angeles") || tz.startsWith("America/Tijuana")) return "US-CA";
  return "OTHER";
}

export function regionRequiresOptIn(region: string): boolean {
  return OPT_IN_REGIONS.has(region) || region === "EU";
}

function defaultStateForRegion(region: string): ConsentState {
  const optIn = regionRequiresOptIn(region);
  return {
    necessary: true,
    analytics: !optIn,
    marketing: !optIn,
    preferences: !optIn,
    policyVersion: CONSENT_POLICY_VERSION,
    decidedAt: null,
    region,
    source: "implicit-default",
  };
}

function readStored(): ConsentState | null {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ConsentState>;
    if (!parsed || typeof parsed !== "object") return null;
    if (parsed.policyVersion !== CONSENT_POLICY_VERSION) return null;
    return {
      necessary: true,
      analytics: !!parsed.analytics,
      marketing: !!parsed.marketing,
      preferences: !!parsed.preferences,
      policyVersion: CONSENT_POLICY_VERSION,
      decidedAt: parsed.decidedAt ?? null,
      region: parsed.region || detectRegion(),
      source: parsed.source || "explicit",
    };
  } catch {
    return null;
  }
}

let cached: ConsentState | null = null;

export function getConsentState(): ConsentState {
  if (cached) return cached;
  const stored = readStored();
  if (stored) {
    cached = stored;
    return stored;
  }
  cached = defaultStateForRegion(detectRegion());
  return cached;
}

export function hasUserDecided(): boolean {
  return !!readStored()?.decidedAt;
}

function pushGtagConsent(state: ConsentState) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  const gtag =
    window.gtag ||
    function gtag() {
      window.dataLayer!.push(arguments);
    };
  window.gtag = gtag;
  gtag("consent", "update", {
    ad_storage: state.marketing ? "granted" : "denied",
    ad_user_data: state.marketing ? "granted" : "denied",
    ad_personalization: state.marketing ? "granted" : "denied",
    analytics_storage: state.analytics ? "granted" : "denied",
    functionality_storage: state.preferences ? "granted" : "denied",
    personalization_storage: state.preferences ? "granted" : "denied",
    security_storage: "granted",
  });
}

// Establish the Consent Mode v2 default *before* any tag fires.
// Must be called as early as possible from the bootstrap (main.tsx).
export function bootstrapConsentDefaults(): void {
  if (typeof window === "undefined") return;
  const state = getConsentState();
  window.dataLayer = window.dataLayer || [];
  const gtag =
    window.gtag ||
    function gtag() {
      window.dataLayer!.push(arguments);
    };
  window.gtag = gtag;
  gtag("consent", "default", {
    ad_storage: state.marketing ? "granted" : "denied",
    ad_user_data: state.marketing ? "granted" : "denied",
    ad_personalization: state.marketing ? "granted" : "denied",
    analytics_storage: state.analytics ? "granted" : "denied",
    functionality_storage: state.preferences ? "granted" : "denied",
    personalization_storage: state.preferences ? "granted" : "denied",
    security_storage: "granted",
    wait_for_update: 500,
  });
}

function persist(state: ConsentState) {
  cached = state;
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(state));
  } catch {}
  pushGtagConsent(state);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: state }));
  }
  // Best-effort server log (audit trail). Don't block UI on failure.
  try {
    void fetch("/api/consent", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({
        surface: "cookie_banner",
        marketing: state.marketing ? 1 : 0,
        analytics: state.analytics ? 1 : 0,
        ads: state.marketing ? 1 : 0,
        policyVersion: state.policyVersion,
        region: state.region,
      }),
    });
  } catch {}
}

export function setConsent(
  partial: Partial<Pick<ConsentState, "analytics" | "marketing" | "preferences">>,
  source: ConsentState["source"] = "explicit",
): ConsentState {
  const current = getConsentState();
  const next: ConsentState = {
    ...current,
    ...partial,
    necessary: true,
    policyVersion: CONSENT_POLICY_VERSION,
    decidedAt: new Date().toISOString(),
    source,
  };
  persist(next);
  return next;
}

export function acceptAll(): ConsentState {
  return setConsent({ analytics: true, marketing: true, preferences: true });
}

export function rejectAll(): ConsentState {
  return setConsent({ analytics: false, marketing: false, preferences: false });
}

export function withdrawAll(): ConsentState {
  return setConsent(
    { analytics: false, marketing: false, preferences: false },
    "withdrawn",
  );
}

export function onConsentChange(handler: (s: ConsentState) => void): () => void {
  if (typeof window === "undefined") return () => {};
  const fn = (e: Event) => {
    const detail = (e as CustomEvent<ConsentState>).detail;
    handler(detail || getConsentState());
  };
  window.addEventListener(CONSENT_EVENT, fn);
  return () => window.removeEventListener(CONSENT_EVENT, fn);
}

export function openCookieSettings(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("rb:open-cookie-settings"));
}
